import { LocalDate } from "@js-joda/core";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import type {
  TimeReportsV2Filter,
  TimeReportsV2Group,
  TimeReportsV2SortBy,
} from "services/api/company/timeReports.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTimeReportsV2Store } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store";
import { formatLocalDateAsApiDate } from "web-apps/company/utils/timeAndDate";
import type {
  TimeReportsV2ListQuery,
  TimeReportsV2SummaryQuery,
} from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import type { TimeReportsGroupByOption } from "web-apps/company/components/timeReportsV2/components/toolbar/timeReportsToolbar.component";

interface UseTimeReportsPersonViewParams {
  readonly dateRange: { fromDate: LocalDate; toDate: LocalDate } | null;
  readonly filters?: TimeReportsV2Filter[];
  readonly groupByOption: TimeReportsGroupByOption;
  readonly isPreferencesHydrated: boolean;
  readonly monthDate: LocalDate;
  readonly workGroupIds: number[];
  readonly workerIds: number[];
}

interface UseTimeReportsPersonViewState {
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly isInlineRefreshing: boolean;
  readonly isLoading: boolean;
  readonly isLoadingMore: boolean;
  readonly loadMoreRef: RefObject<HTMLDivElement>;
  readonly rows: TimeReportsV2Group[];
  readonly total: number;
  readonly refreshAfterInlineEdit: () => void;
  readonly reload: () => void;
}

const PAGE_SIZE = 50;
const INLINE_REFRESH_DELAY_MS = 500;

interface PendingInlineRefresh {
  readonly companyId: number;
  readonly listQuery: TimeReportsV2ListQuery;
  readonly readyAt: number;
}

const resolveSortBy = (
  groupByOption: TimeReportsGroupByOption,
): TimeReportsV2SortBy => {
  return groupByOption === "team" ? "WorkGroup" : "Worker";
};

const normalizeFilters = (
  filters: TimeReportsV2Filter[] | undefined,
): TimeReportsV2Filter[] | undefined => {
  if (!filters || filters.length === 0) return undefined;
  return filters;
};

const serializeNumberList = (values: number[]): string => values.join(",");

const serializeFilterList = (
  filters: TimeReportsV2Filter[] | undefined,
): string => {
  if (!filters || filters.length === 0) return "";
  return filters.join(",");
};

const useStableValueByKey = <T>(value: T, key: string): T => {
  const cacheRef = useRef<{ key: string; value: T }>({ key, value });

  if (cacheRef.current.key !== key) {
    cacheRef.current = { key, value };
  }

  return cacheRef.current.value;
};

export const useTimeReportsPersonView = (
  params: UseTimeReportsPersonViewParams,
): UseTimeReportsPersonViewState => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [timeReportsV2State, timeReportsV2Actions] = useTimeReportsV2Store();

  const companyId = companyState.company?.id;
  const workGroupIdsKey = useMemo(
    () => serializeNumberList(params.workGroupIds),
    [params.workGroupIds],
  );
  const workerIdsKey = useMemo(
    () => serializeNumberList(params.workerIds),
    [params.workerIds],
  );
  const filtersKey = useMemo(
    () => serializeFilterList(params.filters),
    [params.filters],
  );
  const stableWorkGroupIds = useStableValueByKey(
    params.workGroupIds,
    workGroupIdsKey,
  );
  const stableWorkerIds = useStableValueByKey(params.workerIds, workerIdsKey);
  const stableFilters = useStableValueByKey(
    normalizeFilters(params.filters),
    filtersKey,
  );

  const effectiveFromDate = useMemo<LocalDate>(() => {
    return params.dateRange?.fromDate ?? params.monthDate.withDayOfMonth(1);
  }, [params.dateRange, params.monthDate]);

  const effectiveToDate = useMemo<LocalDate>(() => {
    return (
      params.dateRange?.toDate ??
      params.monthDate.withDayOfMonth(params.monthDate.lengthOfMonth())
    );
  }, [params.dateRange, params.monthDate]);

  const fromDate = formatLocalDateAsApiDate(effectiveFromDate);
  const toDate = formatLocalDateAsApiDate(effectiveToDate);

  const listQuery = useMemo<TimeReportsV2ListQuery>(() => {
    return {
      fromDate,
      toDate,
      sortBy: resolveSortBy(params.groupByOption),
      filters: stableFilters,
      workGroupIds: stableWorkGroupIds,
      workerIds: stableWorkerIds,
      pageSize: PAGE_SIZE,
    };
  }, [
    fromDate,
    toDate,
    params.groupByOption,
    stableFilters,
    stableWorkGroupIds,
    stableWorkerIds,
  ]);

  const summaryQuery = useMemo<TimeReportsV2SummaryQuery>(() => {
    return {
      fromDate,
      toDate,
      workGroupIds: stableWorkGroupIds,
      workerIds: stableWorkerIds,
    };
  }, [fromDate, toDate, stableWorkGroupIds, stableWorkerIds]);

  const rows = timeReportsV2State.list?.items ?? [];
  const total = timeReportsV2State.list?.total ?? 0;
  const hasMore = rows.length < total;
  const [pendingInlineRefresh, setPendingInlineRefresh] =
    useState<PendingInlineRefresh | null>(null);
  const inlineRefreshScopeRef = useRef<{
    readonly companyId?: number;
    readonly listQuery: TimeReportsV2ListQuery;
  } | null>(null);
  inlineRefreshScopeRef.current = {
    companyId,
    listQuery,
  };

  useEffect(
    () => () => {
      inlineRefreshScopeRef.current = null;
    },
    [],
  );

  useEffect(() => {
    const pending = pendingInlineRefresh;
    if (!pending) return;
    if (pending.companyId !== companyId || pending.listQuery !== listQuery) {
      setPendingInlineRefresh(null);
      return;
    }
    if (
      timeReportsV2State.isListLoading ||
      timeReportsV2State.isListLoadingMore ||
      timeReportsV2State.isSummaryLoading
    )
      return;
    // Keep the deadline when an in-flight request finishes during the delay.
    const timeoutId = window.setTimeout(
      () => {
        setPendingInlineRefresh(null);
        void timeReportsV2Actions.refreshLoadedList({
          companyId: pending.companyId,
          query: listQuery,
          lastPage: timeReportsV2State.page,
        });
        void timeReportsV2Actions.fetchSummary(pending.companyId, summaryQuery);
        void timeReportsV2Actions.refreshCount(pending.companyId);
      },
      Math.max(0, pending.readyAt - Date.now()),
    );
    return () => window.clearTimeout(timeoutId);
  }, [
    companyId,
    pendingInlineRefresh,
    listQuery,
    summaryQuery,
    timeReportsV2Actions,
    timeReportsV2State.isListLoading,
    timeReportsV2State.isListLoadingMore,
    timeReportsV2State.isSummaryLoading,
    timeReportsV2State.page,
  ]);

  useEffect(() => {
    if (!companyId) {
      timeReportsV2Actions.resetList();
      return;
    }
    if (!params.isPreferencesHydrated) {
      return;
    }
    timeReportsV2Actions.setListQuery(listQuery);
    void timeReportsV2Actions.fetchList(companyId, listQuery, 0, false);
  }, [
    companyId,
    listQuery,
    params.isPreferencesHydrated,
    timeReportsV2Actions,
  ]);

  useEffect(() => {
    if (!companyId) {
      timeReportsV2Actions.setSummaryQuery(null);
      return;
    }
    if (!params.isPreferencesHydrated) {
      return;
    }
    timeReportsV2Actions.setSummaryQuery(summaryQuery);
    void timeReportsV2Actions.fetchSummary(companyId, summaryQuery);
  }, [
    companyId,
    params.isPreferencesHydrated,
    summaryQuery,
    timeReportsV2Actions,
  ]);

  const handleLoadMore = useCallback((): void => {
    if (!companyId || !hasMore || timeReportsV2State.isListLoading) return;
    if (timeReportsV2State.isListLoadingMore) return;

    void timeReportsV2Actions.fetchList(
      companyId,
      listQuery,
      timeReportsV2State.page + 1,
      true,
    );
  }, [
    companyId,
    hasMore,
    listQuery,
    timeReportsV2Actions,
    timeReportsV2State.isListLoading,
    timeReportsV2State.isListLoadingMore,
    timeReportsV2State.page,
  ]);

  const { loadMoreRef } = useInfiniteScroll({
    hasMore: hasMore && params.isPreferencesHydrated,
    isLoading: timeReportsV2State.isListLoadingMore,
    itemCount: rows.length,
    onLoadMore: handleLoadMore,
  });

  return {
    isInlineRefreshing:
      pendingInlineRefresh !== null || timeReportsV2State.isListRefreshing,
    isLoading:
      !params.isPreferencesHydrated || timeReportsV2State.isListLoading,
    isLoadingMore:
      timeReportsV2State.isListLoadingMore &&
      !timeReportsV2State.isListRefreshing,
    error: timeReportsV2State.listError ? t("TimeReports.LoadError") : null,
    rows,
    total,
    hasMore,
    loadMoreRef,
    refreshAfterInlineEdit: (): void => {
      if (!companyId) return;
      const scope = inlineRefreshScopeRef.current;
      if (
        !scope ||
        scope.companyId !== companyId ||
        scope.listQuery !== listQuery
      )
        return;
      setPendingInlineRefresh({
        companyId,
        listQuery,
        readyAt: Date.now() + INLINE_REFRESH_DELAY_MS,
      });
    },
    reload: (): void => {
      if (!companyId) return;
      void timeReportsV2Actions.refreshAll(companyId, {
        listQuery,
        summaryQuery,
      });
    },
  };
};
